import { useLayoutEffect, useRef } from 'react'

/**
 * Réduit un texte jusqu'à ce qu'il tienne dans sa boîte.
 *
 * La taille réglée par l'opérateur est un **maximum** : un titre de programme
 * inhabituellement long rétrécit au lieu d'être tronqué. Avant, la valeur était
 * en `nowrap` + `ellipsis` et la fin de l'information disparaissait sans
 * prévenir — sur un écran de régie, c'est exactement ce qu'on ne veut pas
 * découvrir en direct.
 *
 * La mesure est faite en `useLayoutEffect`, donc avant peinture : aucun
 * scintillement à l'écran. On ne boucle pas — le rapport largeur disponible sur
 * largeur naturelle donne directement le facteur, la police étant homothétique.
 * Une seconde passe rattrape l'arrondi et les cas où la réduction fait remonter
 * une ligne.
 */

/** En deçà, le texte deviendrait illisible : on préfère laisser déborder. */
const MIN_SCALE = 0.35

/** Marge de sécurité : une mesure au pixel près laisse parfois dépasser un jambage. */
const SAFETY = 0.985

export interface FitTextOptions {
  /** Taille maximale, dans l'unité de la feuille de style (`cqw` ici). */
  maxFontCqw: number
  /** `false` désactive la réduction — l'appelant gère le débordement autrement. */
  enabled: boolean
  /** Change de valeur quand le contenu change, pour déclencher une remesure. */
  contentKey: unknown
}

export function useFitText<T extends HTMLElement>(options: FitTextOptions) {
  const ref = useRef<T | null>(null)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    if (!options.enabled) {
      element.style.fontSize = `${options.maxFontCqw}cqw`
      return
    }

    const apply = () => {
      element.style.fontSize = `${options.maxFontCqw}cqw`
      const parent = element.parentElement
      if (!parent) return

      for (let pass = 0; pass < 2; pass += 1) {
        // `scrollWidth` mesure le contenu même débordant ; `clientWidth` la
        // place réellement offerte, padding déduit.
        const available = element.clientWidth
        const natural = element.scrollWidth
        const verticalOverflow = parent.scrollHeight > parent.clientHeight
        if (natural <= available && !verticalOverflow) return

        const widthRatio = natural > 0 ? available / natural : 1
        const heightRatio = parent.scrollHeight > 0 ? parent.clientHeight / parent.scrollHeight : 1
        const ratio = Math.min(widthRatio, verticalOverflow ? heightRatio : 1) * SAFETY
        const current = parseFloat(element.style.fontSize) || options.maxFontCqw
        const next = Math.max(options.maxFontCqw * MIN_SCALE, current * Math.min(1, ratio))
        if (next >= current) return
        element.style.fontSize = `${next}cqw`
      }
    }

    apply()

    // La toile est fluide : un changement de taille de fenêtre change la place
    // disponible sans changer le contenu.
    const observer = new ResizeObserver(apply)
    observer.observe(element)
    if (element.parentElement) observer.observe(element.parentElement)
    return () => observer.disconnect()
  }, [options.enabled, options.maxFontCqw, options.contentKey])

  return ref
}
